import type {
  GuidedFollowUpAnswer,
} from "@/features/blueprints/blueprintTypes";
import {
  GUIDED_TEXT_MAX_LENGTH,
  type GuidedFollowUp,
} from "@/features/blueprints/guided/guidedTypes";

type GuidedFollowUpStepProps = {
  followUp: GuidedFollowUp;
  followUpNumber: number;
  totalFollowUps: number;
  answer: GuidedFollowUpAnswer | undefined;
  saveLabel: string;
  onAnswerChange: (answer: GuidedFollowUpAnswer) => void;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
};

export function GuidedFollowUpStep({
  followUp,
  followUpNumber,
  totalFollowUps,
  answer,
  saveLabel,
  onAnswerChange,
  onBack,
  onNext,
  onSkip,
}: GuidedFollowUpStepProps) {
  const selectedAnswer = answer?.answer ?? "";
  const isUnsure = Boolean(answer?.unsure);

  function setText(value: string) {
    onAnswerChange({ answer: value, skipped: false, unsure: false });
  }

  function selectOption(value: string) {
    onAnswerChange({ answer: value, skipped: false, unsure: false });
  }

  function selectUnsure() {
    onAnswerChange({ answer: "I don't know", skipped: false, unsure: true });
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-indigo-700">
            Follow-up {followUpNumber} of {totalFollowUps}
          </p>
          <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
            Quick clarification
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
            A quick question to make your blueprint stronger.
          </p>
        </div>
        <span className="rounded-md bg-slate-100 px-3 py-1 text-sm font-semibold capitalize text-slate-700">
          {saveLabel}
        </span>
      </div>

      <div className="mt-6 rounded-lg bg-slate-50 p-5">
        <h3 className="text-lg font-semibold text-slate-950">
          {followUp.title}
        </h3>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          {followUp.helper}
        </p>
      </div>

      {followUp.type === "chips" ? (
        <div className="mt-6 flex flex-wrap gap-2">
          {followUp.options?.map((option) => {
            const isSelected = selectedAnswer === option && !isUnsure;

            return (
              <button
                key={option}
                type="button"
                onClick={() => selectOption(option)}
                className={`rounded-md border px-3 py-2 text-sm font-semibold transition ${
                  isSelected
                    ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                }`}
              >
                {option}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="mt-6">
          {followUp.example ? (
            <p className="mb-2 text-sm text-slate-500">{followUp.example}</p>
          ) : null}
          <textarea
            value={isUnsure ? "" : selectedAnswer}
            onChange={(event) => setText(event.target.value)}
            maxLength={GUIDED_TEXT_MAX_LENGTH}
            rows={4}
            disabled={isUnsure}
            className="w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm leading-6 text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-100 disabled:text-slate-500"
          />
          <p className="mt-2 text-xs text-slate-500">
            {(isUnsure ? "" : selectedAnswer).length} / {GUIDED_TEXT_MAX_LENGTH}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={selectUnsure}
        className={`mt-5 rounded-md border px-3 py-2 text-sm font-semibold transition ${
          isUnsure
            ? "border-amber-300 bg-amber-50 text-amber-800"
            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
        }`}
      >
        I don't know
      </button>

      <div className="mt-6 flex flex-wrap justify-between gap-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
        >
          Back
        </button>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onSkip}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={onNext}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
}
